/**
 * Input parsing and display formatting shared by the free HAVS and noise
 * exposure calculators. Pure functions, no I/O.
 */

/** Longest number a calculator field accepts, in characters. */
export const MAX_NUMBER_INPUT_LENGTH = 6;

/**
 * Keeps only digits and the first decimal point, so a half-typed value such
 * as "7." or "" stays in the field instead of snapping to 0 (the F10 checker's
 * snap-to-0 lesson, components/marketing/f10-notifiability-check.tsx).
 */
export function sanitiseDecimalInput(raw: string): string {
  const digitsAndPoints = raw.replace(/[^0-9.]/g, "");
  const firstPoint = digitsAndPoints.indexOf(".");
  const single =
    firstPoint === -1
      ? digitsAndPoints
      : digitsAndPoints.slice(0, firstPoint + 1) + digitsAndPoints.slice(firstPoint + 1).replace(/\./g, "");
  return single.slice(0, MAX_NUMBER_INPUT_LENGTH);
}

/** Digits only (minutes and whole hours). */
export function sanitiseWholeInput(raw: string): string {
  return raw.replace(/[^0-9]/g, "").slice(0, MAX_NUMBER_INPUT_LENGTH);
}

/** A field's value as a number; null when the field is empty or only ".". */
export function parseNumberInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === ".") return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/** Hours and minutes fields as decimal hours. Empty fields count as 0. */
export function durationInHours(hoursRaw: string, minutesRaw: string): number {
  const hours = parseNumberInput(hoursRaw) ?? 0;
  const minutes = parseNumberInput(minutesRaw) ?? 0;
  return hours + minutes / 60;
}

/**
 * A time to reach a limit, in hours, as "46 min", "3 h", "3 h 7 min" or "more
 * than 24 hours". Rounded DOWN to the whole minute, as HSE's noise calculator
 * shows it (L108 Figure 26: 2.0095 hours is shown as 2:00), so the time shown
 * is never later than the real one.
 */
export function formatDuration(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours)) return "not reached";
  const totalMinutes = Math.floor(hours * 60 + 1e-9);
  if (totalMinutes > 24 * 60) return "more than 24 hours";
  if (totalMinutes < 1) return "under 1 min";
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (wholeHours === 0) return `${minutes} min`;
  if (minutes === 0) return `${wholeHours} h`;
  return `${wholeHours} h ${minutes} min`;
}

/**
 * A whole number with comma thousands separators, e.g. 1,250. Done by hand,
 * not with toLocaleString, so the server and browser render the same digits
 * (and the pinned-formatting guardrail stays clean).
 */
export function formatWhole(value: number): string {
  const whole = Math.round(value);
  const digits = Math.abs(whole).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return whole < 0 ? `-${digits}` : digits;
}

/** A value to a fixed number of decimal places, e.g. 3.06. */
export function formatFixed(value: number, places: number): string {
  return value.toFixed(places);
}
